import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Phone, Music } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DjsPage() {
  const navigate = useNavigate();
  const [djs, setDjs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/djs`)
      .then(r => setDjs(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Navigation />
      <div className="pt-32 pb-24">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12">
          {/* Header */}
          <div className="text-center mb-16">
            <button
              onClick={() => navigate(-1)}
              data-testid="djs-back-btn"
              className="inline-flex items-center gap-2 text-sm text-[#8A9A5B] hover:text-[#6b7a45] mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Retour
            </button>
            <div className="flex items-center justify-center gap-3 mb-4">
              <Music className="w-8 h-8 text-[#C4A962]" strokeWidth={1.5} />
            </div>
            <h1 data-testid="djs-page-title" className="font-['Playfair_Display'] text-3xl md:text-5xl text-[#2C3E30] tracking-tight">
              DJ partenaires
            </h1>
            <p className="mt-4 text-[#595959] text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              Pour animer vos mariages, anniversaires et soirees dans la salle des fetes, le Domaine du Pilat vous recommande ses DJ et animateurs partenaires.
            </p>
          </div>

          {/* DJ list */}
          {loading ? (
            <div className="text-center py-16">
              <div className="w-8 h-8 border-2 border-[#8A9A5B] border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : djs.length === 0 ? (
            <div data-testid="djs-empty" className="text-center py-16 bg-white rounded-2xl border border-[#E5E5E0]">
              <Music className="w-12 h-12 text-[#C4A962]/40 mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-[#595959]">La liste des DJ partenaires sera bientot disponible.</p>
              <p className="text-sm text-[#595959]/70 mt-2">Contactez-nous pour toute recommandation.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {djs.map(dj => (
                <div
                  key={dj.id}
                  data-testid={`dj-card-${dj.id}`}
                  className="bg-white rounded-2xl border border-[#E5E5E0] p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start gap-4">
                    {dj.logo_url ? (
                      <img
                        src={dj.logo_url}
                        alt={dj.name}
                        className="w-16 h-16 rounded-xl object-cover flex-shrink-0 border border-[#E5E5E0]"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-xl bg-[#F5F5F0] flex items-center justify-center flex-shrink-0">
                        <Music className="w-7 h-7 text-[#C4A962]" strokeWidth={1.5} />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-[#2C3E30] text-lg">{dj.name}</h3>
                      {dj.specialties && (
                        <p className="text-xs text-[#8A9A5B] mt-0.5">{dj.specialties}</p>
                      )}
                      {dj.description && (
                        <p className="text-sm text-[#595959] mt-2 line-clamp-3">{dj.description}</p>
                      )}
                      <div className="flex items-center gap-3 mt-3">
                        {dj.website_url && (
                          <a
                            href={dj.website_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm text-[#C4A962] hover:text-[#b5945a] transition-colors"
                          >
                            <ExternalLink className="w-3.5 h-3.5" /> Site web
                          </a>
                        )}
                        {dj.phone && (
                          <a
                            href={`tel:${dj.phone}`}
                            className="inline-flex items-center gap-1 text-sm text-[#8A9A5B] hover:text-[#6b7a45] transition-colors"
                          >
                            <Phone className="w-3.5 h-3.5" /> {dj.phone}
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* CTA */}
          <div className="text-center mt-16">
            <Button
              data-testid="djs-reserve-btn"
              onClick={() => navigate('/#reservation')}
              className="rounded-full px-8 py-3 bg-[#8A9A5B] text-white hover:bg-[#7a8a4d] text-base"
            >
              Reserver maintenant
            </Button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
